import React, { useState, useEffect, useRef } from 'react';
import { FaPrint, FaBarcode, FaTimes } from 'react-icons/fa';
import styles from './styles/StockManager.module.css';
import { useAuth } from '../../context/AuthContext';
import { nombreOperativo } from '../../utils/productName';

interface Product {
    idProductos: number;
    NombresProductos: string;
    NombreEtiqueta?: string | null;
    barcode?: string;
    CodigoBarras?: number | string;
}

type ScanFeedback = { type: 'ok' | 'err'; text: string } | null;

interface Local {
    locales_id: number;
    nombre_local: string;
}

const RemitoElectronico: React.FC = () => {
    const { user, defaultLocalId } = useAuth();
    const [products, setProducts] = useState<Product[]>([]);
    const [locales, setLocales] = useState<Local[]>([]);

    const [transferSource, setTransferSource] = useState<number | null>(null);
    const [transferDest, setTransferDest] = useState<number | null>(null);
    const [transferCart, setTransferCart] = useState<{ product: Product; quantity: number }[]>([]);
    const [transferSearch, setTransferSearch] = useState('');
    const [lastTransferId, setLastTransferId] = useState<string | null>(null);
    const [scanFeedback, setScanFeedback] = useState<ScanFeedback>(null);
    const searchRef = useRef<HTMLInputElement>(null);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

    useEffect(() => {
        fetch(`${API_URL}/productos`)
            .then(r => r.json())
            .then(d => { if (Array.isArray(d)) setProducts(d); })
            .catch(console.error);

        fetch(`${API_URL}/locales`)
            .then(r => r.json())
            .then(d => { if (Array.isArray(d)) setLocales(d); })
            .catch(console.error);
    }, []);

    // Auto-selección para Carla o usuarios de Fábrica
    useEffect(() => {
        if (locales.length === 0) return;
        const isCarla = user?.toLowerCase() === 'carla';
        const currentLocal = locales.find(l => l.locales_id === defaultLocalId);
        const isFabrica = currentLocal?.nombre_local.toLowerCase().includes('fabrica');

        if (isCarla) {
            const fabrica = locales.find(l => l.nombre_local.toLowerCase().includes('fabrica'));
            const tresDeFebrero = locales.find(l => l.nombre_local.toLowerCase().includes('3 de febrero'));
            if (fabrica) setTransferSource(fabrica.locales_id);
            if (tresDeFebrero) setTransferDest(tresDeFebrero.locales_id);
        } else if (isFabrica) {
            setTransferSource(defaultLocalId);
            const dest = locales.find(l => l.nombre_local.toLowerCase().includes('3 de febrero'));
            if (dest) setTransferDest(dest.locales_id);
        } else {
            // Sin regla especifica: se abre la primera opcion en vez de dejar
            // el selector vacio esperando una eleccion manual.
            setTransferSource(prev => prev ?? defaultLocalId ?? locales[0].locales_id);
            setTransferDest(prev => {
                if (prev != null) return prev;
                const origen = defaultLocalId ?? locales[0].locales_id;
                // El destino no puede ser el mismo local que el origen.
                return (locales.find(l => l.locales_id !== origen) ?? locales[0]).locales_id;
            });
        }
    }, [locales, defaultLocalId, user]);

    // Suma una cantidad al carrito (reutilizado por click de búsqueda y por escáner)
    const addProductToCart = (product: Product, qty: number = 1) => {
        setTransferCart(prev => {
            const existing = prev.find(item => item.product.idProductos === product.idProductos);
            if (existing) {
                return prev.map(item =>
                    item.product.idProductos === product.idProductos
                        ? { ...item, quantity: item.quantity + qty }
                        : item
                );
            }
            return [...prev, { product, quantity: qty }];
        });
        setTransferSearch('');
    };

    // Escaneo: el lector USB escribe el código y manda Enter. Buscamos por
    // barcode / CodigoBarras (o id). Acepta cualquiera de los dos códigos.
    const handleScan = (raw: string) => {
        const value = raw.trim();
        if (!value) return;

        const product = products.find(p =>
            (p.barcode && p.barcode.toLowerCase() === value.toLowerCase()) ||
            (p.CodigoBarras != null && String(p.CodigoBarras) === value) ||
            String(p.idProductos) === value
        );

        if (!product) {
            setScanFeedback({ type: 'err', text: `Código "${value}" no reconocido` });
            setTransferSearch('');
            setTimeout(() => setScanFeedback(null), 2500);
            setTimeout(() => searchRef.current?.focus(), 0);
            return;
        }

        addProductToCart(product, 1);
        setScanFeedback({ type: 'ok', text: `${nombreOperativo(product)} ✓ (+1)` });
        setTimeout(() => setScanFeedback(null), 2000);
        setTimeout(() => searchRef.current?.focus(), 0);
    };

    const removeFromTransferCart = (id: number) => {
        setTransferCart(prev => prev.filter(item => item.product.idProductos !== id));
    };

    const updateTransferCartQuantity = (id: number, newQty: number) => {
        if (newQty < 1) return;
        setTransferCart(prev =>
            prev.map(item => item.product.idProductos === id ? { ...item, quantity: newQty } : item)
        );
    };

    const handleBulkTransfer = async () => {
        if (!transferSource || !transferDest || transferCart.length === 0) {
            setError('Complete todos los campos y agregue productos');
            return;
        }
        if (transferSource === transferDest) {
            setError('Origen y destino deben ser distintos');
            return;
        }
        setLoading(true);
        setError(null);
        setSuccess(null);
        try {
            const response = await fetch(`${API_URL}/admin/stock/transfer-bulk`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    items: transferCart.map(item => ({
                        id_producto: item.product.idProductos,
                        cantidad: item.quantity
                    })),
                    id_local_origen: transferSource,
                    id_local_destino: transferDest,
                    usuario: user || 'Admin'
                })
            });
            const data = await response.json();
            if (response.ok && data.transfer_id) {
                setLastTransferId(data.transfer_id);
                setSuccess('Traslado realizado con éxito');
                const remitoData = {
                    id: data.transfer_id,
                    origen: locales.find(l => l.locales_id === transferSource)?.nombre_local || '',
                    destino: locales.find(l => l.locales_id === transferDest)?.nombre_local || '',
                    fecha: new Date().toISOString(),
                    items: transferCart.map(item => ({
                        producto: nombreOperativo(item.product),
                        cantidad: item.quantity
                    })),
                    usuario: user || 'Admin'
                };
                (window as any)._lastRemito = remitoData;
                setTransferCart([]);
                setTransferSearch('');
            } else {
                setError(data.error || 'Error al realizar el traslado');
            }
        } catch {
            setError('Error de conexión al realizar el traslado');
        } finally {
            setLoading(false);
            setTimeout(() => { setError(null); setSuccess(null); }, 5000);
        }
    };

    return (
        <div className={styles['stock-container']}>
            <h3>Remito Electrónico</h3>

            {error && <div className={styles['error-msg']} style={{ marginBottom: '15px' }}>{error}</div>}
            {success && <div className={styles['success-msg']} style={{ marginBottom: '15px' }}>{success}</div>}

            <div className={styles['transfer-cart-container']}>
                <h4>Traslado de Mercadería</h4>
                <div className={styles.row} style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
                    <div className={styles['form-group']} style={{ flex: 1 }}>
                        <label>Origen</label>
                        <select value={transferSource || ''} onChange={(e) => setTransferSource(parseInt(e.target.value))}>
                            <option value="">Seleccione Origen</option>
                            {locales.map(l => (
                                <option key={l.locales_id} value={l.locales_id}>{l.nombre_local}</option>
                            ))}
                        </select>
                    </div>
                    <div className={styles['form-group']} style={{ flex: 1 }}>
                        <label>Destino</label>
                        <select value={transferDest || ''} onChange={(e) => setTransferDest(parseInt(e.target.value))}>
                            <option value="">Seleccione Destino</option>
                            {locales.map(l => (
                                <option key={l.locales_id} value={l.locales_id}>{l.nombre_local}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className={styles['scanner-input-wrapper']}>
                    <FaBarcode className={styles['scanner-icon']} />
                    <input
                        ref={searchRef}
                        type="text"
                        placeholder="Escaneá un código o buscá por nombre..."
                        value={transferSearch}
                        onChange={(e) => setTransferSearch(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                handleScan(transferSearch);
                            }
                        }}
                        autoComplete="off"
                        autoFocus
                        className={styles['scanner-input']}
                    />
                    {transferSearch.length > 1 && (
                        <div className={styles['search-dropdown-pos']}>
                            {products
                                .filter(p => nombreOperativo(p).toLowerCase().includes(transferSearch.toLowerCase()) || p.NombresProductos.toLowerCase().includes(transferSearch.toLowerCase()))
                                .slice(0, 8)
                                .map(p => (
                                    <div
                                        key={p.idProductos}
                                        className={styles['search-item-pos']}
                                        onClick={() => addProductToCart(p, 1)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <span>{nombreOperativo(p)}</span>
                                    </div>
                                ))}
                        </div>
                    )}
                </div>

                {scanFeedback && (
                    <div style={{
                        margin: '10px 0',
                        padding: '8px 14px',
                        borderRadius: '6px',
                        fontWeight: 'bold',
                        color: 'white',
                        backgroundColor: scanFeedback.type === 'ok' ? '#2e7d32' : '#c62828'
                    }}>
                        {scanFeedback.text}
                    </div>
                )}

                {transferCart.length > 0 && (
                    <>
                        <div className={styles['cart-table-container']}>
                            <table className={styles['cart-table-pos']}>
                                <thead>
                                    <tr>
                                        <th>Producto</th>
                                        <th style={{ textAlign: 'center' }}>Cantidad</th>
                                        <th style={{ textAlign: 'right' }}>Acción</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {transferCart.map(item => (
                                        <tr key={item.product.idProductos}>
                                            <td>{nombreOperativo(item.product)}</td>
                                            <td style={{ textAlign: 'center' }}>
                                                <input
                                                    type="number"
                                                    value={item.quantity}
                                                    onChange={(e) => updateTransferCartQuantity(item.product.idProductos, parseInt(e.target.value) || 1)}
                                                    className={styles['qty-input-mini']}
                                                    style={{ width: '70px', textAlign: 'center', margin: '0 auto' }}
                                                />
                                            </td>
                                            <td style={{ textAlign: 'right' }}>
                                                <button
                                                    onClick={() => removeFromTransferCart(item.product.idProductos)}
                                                    className={styles['remove-btn-pos']}
                                                >
                                                    <FaTimes />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <button
                            className={styles['finalize-btn-pos']}
                            onClick={handleBulkTransfer}
                            disabled={loading}
                        >
                            {loading ? 'Procesando...' : 'FINALIZAR TRASLADO'}
                        </button>
                    </>
                )}

                {lastTransferId && (
                    <div style={{ marginTop: '20px', textAlign: 'center' }}>
                        <button
                            onClick={() => {
                                const { generateRemitoPDF } = require('../../services/pdfService');
                                if ((window as any)._lastRemito) {
                                    generateRemitoPDF((window as any)._lastRemito);
                                }
                            }}
                            style={{ padding: '12px 25px', backgroundColor: '#0277bd', color: 'white', border: 'none', borderRadius: '5px', display: 'inline-flex', alignItems: 'center', gap: '10px', fontWeight: 'bold', cursor: 'pointer' }}
                        >
                            <FaPrint /> Descargar Remito #{lastTransferId}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default RemitoElectronico;
